import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { StopClickPropagationDirective } from '@directives/stop-click-propagation/stop-click-propagation.directive';
import { BusinessPlansParam } from '@models/customize-service.model';
import { createShoppingObject, stringOnlyHTML } from '@models/functions.model';
import { ShoppingCartData, ShoppingCartItem } from '@models/shopping-cart.model';
import {
  CART_ITEM_TYPE_FEATURE,
  CART_ITEM_TYPE_PLAN,
  ManageBusinessPlans,
  PlanType
} from '@models/shopping.model';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslationPipe } from '@pipes/translation/translation.pipe';
import { ActivityManagerService } from '@services/activity/manager/activity-manager.service';
import { CustomizeService } from '@services/customize-service/customize.service';
import { ModalService } from '@services/modal/modal.service';
import { ShoppingService } from '@services/shopping/shopping.service';
import { ViewDetailsComponent } from '@shared-components/view-details/view-details.component';
import { Observable, Subscription, takeUntil } from 'rxjs';
import { DataLoaderDirective } from 'tangoe-design-system';

import { AdminOverrideBadgeComponent } from '../../admin-override/admin-override-badge/admin-override-badge.component';

@Component({
  selector: 'ts-business-plans',
  templateUrl: './business-plans.component.html',
  imports: [
    TranslationPipe,
    DataLoaderDirective,
    StopClickPropagationDirective,
    AdminOverrideBadgeComponent
  ]
})
export class BusinessPlansComponent implements OnInit, OnDestroy {
  shopping_cart: ShoppingCartData;
  plan_state: BusinessPlansParam;
  plan_id: number;
  business_plans: PlanType[] = [];
  no_plans: boolean;
  private subscriptions: Subscription = new Subscription();

  constructor(
    private activity_svc: ActivityManagerService,
    private customize_svc: CustomizeService,
    private modal_svc: ModalService,
    private shopping_svc: ShoppingService
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.customize_svc.cartChanges.subscribe(
        (res: ShoppingCartData) => (this.shopping_cart = { ...res })
      )
    );

    this.subscriptions.add(
      this.customize_svc.state['plans_param']?.subscribe((res: BusinessPlansParam) => {
        this.shopping_cart = this.customize_svc.getCart();
        this.plan_state = res!;
        this.getBusinessPlans();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get plan_on_cart(): ShoppingCartItem | undefined {
    return this.shopping_cart.shopping_cart_items.find((i: ShoppingCartItem) =>
      [CART_ITEM_TYPE_PLAN].includes(i.type)
    );
  }

  get select_plan_on_cart(): PlanType | undefined {
    return this.business_plans.find((p: PlanType) => p.id == this.plan_on_cart?.id);
  }

  sanitizedHTML(description: string | undefined): boolean {
    return !stringOnlyHTML(description) && description !== '';
  }

  selectPlan(plan: PlanType): void {
    if (this.plan_id == plan.id) return;

    this.plan_id = plan.id;
    this.addToCart(plan);
    setTimeout(() => {
      this.customize_svc.setState('features_param', {
        orderTypeId: this.shopping_cart.orderTypeId,
        carrierId: this.shopping_cart.carrier?.id ?? 0,
        lineId: this.shopping_cart.lineId,
        planId: plan.id,
        include_admin_options: this.shopping_cart.device_include_admin_options
      });
    }, 100);
  }

  openDetailsModal(p: PlanType, body: string): void {
    let active_element: HTMLElement = document.activeElement as HTMLElement;
    active_element.blur();

    const modalRef: NgbModalRef = this.modal_svc.open(ViewDetailsComponent, {
      backdrop: 'static',
      centered: true,
      scrollable: true,
      size: 'lg',
      modalDialogClass: 'scrollable-modal-maxheight'
    });
    modalRef.componentInstance.modal_title = p.name_override ? p.name_override : p.name;
    modalRef.componentInstance.modal_body = body;
  }

  private getBusinessPlans(): void {
    const param: { [key: string]: number | string | boolean } = {
      lineId: this.plan_state.lineId!
    };

    if (this.plan_state.ref_device_id != undefined)
      param['ref_device_id'] = this.plan_state.ref_device_id;

    if (this.plan_state.include_admin_options != undefined)
      param['include_admin_options'] = this.plan_state.include_admin_options;

    this.shopping_svc
      .getPlans(this.plan_state.carrierId, param)
      .pipe(takeUntil(this.customize_svc.state.plans_param as Observable<BusinessPlansParam>))
      .subscribe({
        next: (res: ManageBusinessPlans) => {
          this.plan_id = -1;
          this.activity_svc.state().setSteps(res.progress_bar_config?.steps);
          this.customize_svc.state.extras?.next({ number_of_plans: res.plans.length });
          this.business_plans = res.plans;
          this.no_plans = res.plans.length === 0;

          if (this.business_plans.length === 1) this.selectPlan(this.business_plans[0]);
          else if (this.select_plan_on_cart) this.selectPlan(this.select_plan_on_cart);
        },
        error: (err: HttpErrorResponse) => {}
      });
  }

  private addToCart(plan: PlanType): void {
    if (this.plan_on_cart)
      this.shopping_cart.shopping_cart_items = this.shopping_cart.shopping_cart_items.filter(
        (i: ShoppingCartItem) =>
          ![
            CART_ITEM_TYPE_PLAN,
            ...(this.plan_on_cart?.id != plan.id ? [CART_ITEM_TYPE_FEATURE] : [])
          ].includes(i.type)
      );

    this.shopping_cart.shopping_cart_items.push(
      createShoppingObject(plan, CART_ITEM_TYPE_PLAN, this.shopping_cart.carrier?.image_url)
    );
    this.customize_svc.setCart(this.shopping_cart);
  }
}
